'use client'

import { Box, Flex, ScrollArea } from '@mantine/core'
import { Suspense, useMemo, useState } from 'react'

import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import { SectionErrorBoundary } from '@/components/error/boundary'
import { useSsrContext } from '@/contexts/ssr'
import { useCoinPricesBySlug } from '@/services/coin/prices'

import BasicLineChart from './components/BasicLineChart'
import ChartInfoTitle from './components/ChartInfoTitle'
import ChartSkeleton from './components/ChartSkeleton'
import CoinTitle from './components/CoinTitle'
import SettingButton from './components/SettingButton'
import TimeRangeItem from './components/TimeRangeItem'
import TradingViewChart from './components/TradingViewChart'
import { useTitle } from './hooks'
import { BTC_SLUG, SettingType, TIME_RANGE_OPTIONS } from './utils'

interface CoinLineChartProps {
  customHeader?: React.ReactNode
  renderInfoTitle?: (props: {
    coinPrice?: number
    coinPercentChange?: number
    btcPrice?: number
    isMouseInChart: boolean
    curTimeRange?: string
  }) => React.ReactNode
}

function CoinLineChartContent({ customHeader, renderInfoTitle }: CoinLineChartProps) {
  const { coin } = useCoinContext()
  const { slug } = coin
  const isBtc = slug === BTC_SLUG
  const isShowTradingView = coin.trading_view_status === 1
  const [curTimeRange, setCurTimeRange] = useState('24H')
  const [settingType, setSettingType] = useState<SettingType | null>(null)
  const queryParams = useMemo(() => {
    const targetOption = TIME_RANGE_OPTIONS.find(option => option.value === curTimeRange)
    return targetOption!.getQueryParams()
  }, [curTimeRange])
  const { selectedCurrency } = useSsrContext()
  const { data: currentCoinPrices, isFetching: isFetchingCurrentCoinPrices } = useCoinPricesBySlug({
    slug,
    ...queryParams,
    convert: selectedCurrency,
  })
  const { data: btcCoinPrices, isFetching: isFetchingBtcCoinPrices } = useCoinPricesBySlug({
    slug,
    ...queryParams,
    convert: 'btc',
  })

  const btcPricesVisible = settingType === SettingType.BTC_PRICE
  const { setMousePointData, coinPrice, coinPriceChange, btcPrice, isMouseInChart } = useTitle({
    btcPricesVisible,
    currentCoinPrices,
  })
  return (
    <Box flex={1}>
      <CoinTitle
        currentCoinPrices={currentCoinPrices}
        curTimeRange={curTimeRange}
        slug={slug}
        customHeader={customHeader}
      />
      {renderInfoTitle ? (
        renderInfoTitle({
          coinPrice,
          coinPercentChange: coinPriceChange,
          btcPrice,
          isMouseInChart,
          curTimeRange,
        })
      ) : (
        <ChartInfoTitle
          btcPrice={btcPrice}
          coinPrice={coinPrice}
          coinPercentChange={coinPriceChange}
          isMouseInChart={isMouseInChart}
        />
      )}
      {settingType === SettingType.TRADING_VIEW ? (
        <TradingViewChart />
      ) : isFetchingCurrentCoinPrices || isFetchingBtcCoinPrices ? (
        <ChartSkeleton />
      ) : (
        <BasicLineChart
          key={curTimeRange}
          btcPricesVisible={btcPricesVisible}
          btcCoinPrices={btcCoinPrices}
          currentCoinPrices={currentCoinPrices}
          onShowPriceTooltip={setMousePointData}
        />
      )}

      <Flex align={'center'} h={36}>
        <SettingButton
          settingType={settingType}
          onSettingChange={setSettingType}
          bTCPriceButtonVisible={!isBtc}
          isShowTradingView={isShowTradingView}
        />
        <ScrollArea>
          <Flex>
            {settingType !== SettingType.TRADING_VIEW &&
              TIME_RANGE_OPTIONS.map(item => (
                <TimeRangeItem
                  key={item.value}
                  isActive={curTimeRange === item.value}
                  value={item.value}
                  label={item.label}
                  onClick={setCurTimeRange}
                />
              ))}
          </Flex>
        </ScrollArea>
      </Flex>
    </Box>
  )
}

function CoinLineChart(props: CoinLineChartProps) {
  return (
    <Suspense fallback={<ChartSkeleton />}>
      <CoinLineChartContent {...props} />
    </Suspense>
  )
}

export default SectionErrorBoundary.with(CoinLineChart)
